// @ts-check
// Assistente de importação de planilhas (doc 14, seção 9; docs/arquitetura-appcrm/08-contrato-importacao.md):
// arquivo, aba, colunas, revisão, envio em lotes e relatório. Abre pela barra da view de cada objeto.
import { html } from 'htm/preact';
import { createPortal } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Button, IconButton } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { mutationCounterState } from '../../data/record-store.js';
import { downloadCsv, toFileName } from '../../lib/csv.js';
import { MAXIMUM_IMPORT_ROWS, resolveDuplicateCriteria, suggestColumnMapping } from '../../../../shared/spreadsheet-import/index.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import {
  FAMILY_TEMPLATE_OBJECTS, buildErrorCsv, buildRows, buildTargetsForMode, issueMessage, recordImportCounterState, sendImport, serverErrorMessage,
} from './import-model.js';
import { UploadStep } from './upload-step.js';
import { MatchColumnsStep } from './match-columns-step.js';
import { ReviewStep } from './review-step.js';
import { ImportProgress, ImportReport } from './result-step.js';

/**
 * @typedef {import('./import-model.js').ImportMode} ImportMode
 * @typedef {'upload' | 'selectSheet' | 'matchColumns' | 'validate' | 'importing' | 'report'} ImportStep
 */

const STEP_INDICATORS = [
  ['upload', ['upload', 'selectSheet']], ['matchColumns', ['matchColumns']], ['validate', ['validate']], ['import', ['importing', 'report']],
];

/**
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, onClose: () => void }} props
 */
export function SpreadsheetImportDialog({ objectMetadata, onClose }) {
  const canUseFamilyTemplate = FAMILY_TEMPLATE_OBJECTS.has(objectMetadata.nameSingular);
  const [mode, setMode] = useState(/** @type {ImportMode} */ ('object'));
  const [step, setStep] = useState(/** @type {ImportStep} */ ('upload'));
  const [file, setFile] = useState(/** @type {{ name: string, sheets: import('./import-model.js').Sheet[] } | null} */ (null));
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [mapping, setMapping] = useState(/** @type {Array<string | null>} */ ([]));
  const [valueMappings, setValueMappings] = useState(/** @type {Record<number, Record<string, string>>} */ ({}));
  const [createMissing, setCreateMissing] = useState(/** @type {Set<string>} */ (new Set()));
  const [duplicateStrategy, setDuplicateStrategy] = useState('SKIP');
  const [fileError, setFileError] = useState(/** @type {string | null} */ (null));
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [isStopping, setIsStopping] = useState(false);
  const [outcome, setOutcome] = useState(/** @type {Awaited<ReturnType<typeof sendImport>> | null} */ (null));
  const stopRequestedRef = useRef(false);

  const targets = useMemo(() => buildTargetsForMode(mode, objectMetadata), [mode, objectMetadata]);
  const criteria = useMemo(() => (mode === 'object' ? resolveDuplicateCriteria(/** @type {any} */ (objectMetadata)) : []), [mode, objectMetadata]);
  const [criteriaKeys, setCriteriaKeys] = useState(() => resolveDuplicateCriteria(/** @type {any} */ (objectMetadata)).map((criterion) => criterion.key));
  const rows = file?.sheets[sheetIndex]?.rows ?? [];
  const headers = rows[headerRowIndex] ?? [];
  const dataRows = useMemo(() => rows.slice(headerRowIndex + 1), [rows, headerRowIndex]);
  const firstRowNumber = headerRowIndex + 2;
  const objectLabel = translateMetadataLabel(objectMetadata.labelPlural);

  // só monta as linhas a partir da revisão: no mapeamento, cada troca de coluna remontaria até 10.000 linhas
  const isReviewing = step === 'validate' || step === 'importing' || step === 'report';
  const builtRows = useMemo(() => (isReviewing
    ? buildRows({ mode, objectMetadata, dataRows, firstRowNumber, mapping, valueMappings, targets, duplicateCriteriaKeys: criteriaKeys })
    : []), [isReviewing, mode, dataRows, mapping, valueMappings, targets, criteriaKeys]);
  const validRows = builtRows.filter((row) => row.errors.length === 0);

  useEffect(() => {
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || step === 'importing') return;
      event.stopPropagation();
      close();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [step]);

  const close = () => {
    if (step === 'importing') return;
    if (outcome) {
      // a lista do objeto e os quadros recarregam com o que entrou
      recordImportCounterState.value += 1;
      mutationCounterState.value += 1;
    }
    onClose();
  };

  /** @param {number} index @param {{ name: string, sheets: import('./import-model.js').Sheet[] }} [loadedFile] */
  const applySheet = (index, loadedFile = /** @type {any} */ (file)) => {
    const sheetRows = loadedFile.sheets[index]?.rows ?? [];
    if (sheetRows.length - 1 > MAXIMUM_IMPORT_ROWS) {
      setFileError(t('spreadsheetImport.upload.tooManyRows', { count: (sheetRows.length - 1).toLocaleString('pt-BR') }));
      setStep('upload');
      return;
    }
    setFileError(null);
    setSheetIndex(index);
    applyHeaderRow(0, sheetRows);
    setStep('matchColumns');
  };

  /** @param {number} index @param {string[][]} [sheetRows] */
  const applyHeaderRow = (index, sheetRows = rows) => {
    setHeaderRowIndex(index);
    setMapping(suggestColumnMapping(sheetRows[index] ?? [], targets).mapping);
    setValueMappings({});
  };

  const changeMode = (/** @type {ImportMode} */ nextMode) => {
    setMode(nextMode);
    setCreateMissing(new Set());
    setDuplicateStrategy('SKIP');
  };

  const startImport = async () => {
    stopRequestedRef.current = false;
    setIsStopping(false);
    setProgress({ done: 0, total: validRows.length });
    setStep('importing');
    const relationNames = [...createMissing].map((key) => {
      const target = targets.find((candidate) => candidate.key === key);
      if (!target) return key;
      return mode === 'family' ? `${target.group}.${target.fieldName}` : target.fieldName;
    });
    const result = await sendImport({
      mode, objectMetadata, rows: validRows, duplicateStrategy, duplicateCriteriaKeys: criteriaKeys, createMissingRelations: relationNames,
      onProgress: (done) => setProgress({ done, total: validRows.length }),
      shouldStop: () => stopRequestedRef.current,
    });
    setOutcome(result);
    setStep('report');
  };

  const failures = useMemo(() => {
    if (!outcome) return [];
    const clientFailures = builtRows.filter((row) => row.errors.length > 0)
      .map((row) => ({ rowNumber: row.rowNumber, message: row.errors.map(issueMessage).join('; ') }));
    const serverFailures = outcome.results.filter((result) => result.status === 'FAILED')
      .map((result) => ({ rowNumber: result.rowNumber, message: serverErrorMessage(result, targets) }));
    return [...clientFailures, ...serverFailures];
  }, [outcome]);

  const summary = useMemo(() => {
    const counts = { created: 0, updated: 0, skipped: 0, failed: builtRows.filter((row) => row.errors.length > 0).length };
    for (const result of outcome?.results ?? []) {
      if (result.status === 'CREATED') counts.created += 1;
      else if (result.status === 'UPDATED') counts.updated += 1;
      else if (result.status === 'SKIPPED') counts.skipped += 1;
      else if (result.status === 'FAILED') counts.failed += 1;
    }
    return counts;
  }, [outcome]);

  const restart = () => {
    setFile(null);
    setOutcome(null);
    setMapping([]);
    setStep('upload');
    recordImportCounterState.value += 1;
    mutationCounterState.value += 1;
  };

  const currentIndicator = STEP_INDICATORS.findIndex(([, steps]) => steps.includes(step));
  const title = mode === 'family' ? t('spreadsheetImport.familyTitle') : t('spreadsheetImport.title', { object: objectLabel });

  /** @type {any} */
  let body = null;
  /** @type {any} */
  let footer = null;
  if (step === 'upload') {
    body = html`<${UploadStep} objectMetadata=${objectMetadata} mode=${mode} canUseFamilyTemplate=${canUseFamilyTemplate} targets=${targets}
      onModeChange=${changeMode}
      onLoaded=${(/** @type {{ name: string, sheets: import('./import-model.js').Sheet[] }} */ loaded) => {
        setFile(loaded);
        if (loaded.sheets.length > 1) {
          setFileError(null);
          setStep('selectSheet');
        } else applySheet(0, loaded);
      }} />
      ${fileError ? html`<div class="spreadsheet-import-banner" data-variant="error" role="alert"><${Icon} name="IconAlertTriangle" size=${16} />${fileError}</div>` : null}`;
  } else if (step === 'selectSheet' && file) {
    body = html`<div class="spreadsheet-import-sheets">
      <h3 class="spreadsheet-import-step-title">${t('spreadsheetImport.sheet.title')}</h3>
      ${file.sheets.map((sheet, index) => html`<button type="button" class="spreadsheet-import-sheet" key=${index} onClick=${() => applySheet(index)}>
        <${Icon} name="IconTable" size=${16} />
        <span class="spreadsheet-import-sheet-name">${sheet.name}</span>
        <span class="spreadsheet-import-hint">${t('spreadsheetImport.sheet.rows', { count: Math.max(sheet.rows.length - 1, 0) })}</span>
      </button>`)}
    </div>`;
    footer = html`<${Button} title=${t('common.back')} size="small" variant="secondary" onClick=${() => setStep('upload')} />`;
  } else if (step === 'matchColumns') {
    body = html`<${MatchColumnsStep} mode=${mode} rows=${rows} headerRowIndex=${headerRowIndex} onHeaderRowChange=${(/** @type {number} */ index) => applyHeaderRow(index)}
      targets=${targets} mapping=${mapping}
      onMappingChange=${(/** @type {number} */ columnIndex, /** @type {string | null} */ targetKey) => setMapping((current) => current.map((key, index) => (index === columnIndex ? targetKey : key)))}
      valueMappings=${valueMappings}
      onValueMappingChange=${(/** @type {number} */ columnIndex, /** @type {string} */ normalized, /** @type {string} */ value) =>
        setValueMappings((current) => ({ ...current, [columnIndex]: { ...(current[columnIndex] ?? {}), [normalized]: value } }))}
      createMissing=${createMissing}
      onCreateMissingChange=${(/** @type {string} */ key, /** @type {boolean} */ value) => setCreateMissing((current) => {
        const next = new Set(current);
        if (value) next.add(key);
        else next.delete(key);
        return next;
      })} />`;
    footer = html`
      <${Button} title=${t('common.back')} size="small" variant="secondary" onClick=${() => setStep(file && file.sheets.length > 1 ? 'selectSheet' : 'upload')} />
      <${Button} title=${t('spreadsheetImport.steps.validate')} icon="IconArrowRight" size="small" accent="blue" disabled=${!mapping.some(Boolean) || dataRows.length === 0}
        onClick=${() => setStep('validate')} />`;
  } else if (step === 'validate') {
    body = html`<${ReviewStep} mode=${mode} objectMetadata=${objectMetadata} dataRows=${dataRows} firstRowNumber=${firstRowNumber} mapping=${mapping}
      targets=${targets} builtRows=${builtRows} duplicateStrategy=${duplicateStrategy} onDuplicateStrategyChange=${setDuplicateStrategy}
      criteria=${criteria} criteriaKeys=${criteriaKeys} onCriteriaKeysChange=${setCriteriaKeys} />`;
    footer = html`
      <${Button} title=${t('common.back')} size="small" variant="secondary" onClick=${() => setStep('matchColumns')} />
      <${Button} title=${t('spreadsheetImport.validate.submit', { count: validRows.length })} icon="IconFileImport" size="small" accent="blue"
        disabled=${validRows.length === 0} onClick=${startImport} />`;
  } else if (step === 'importing') {
    body = html`<${ImportProgress} done=${progress.done} total=${progress.total} isStopping=${isStopping}
      onStop=${() => {
        stopRequestedRef.current = true;
        setIsStopping(true);
      }} />`;
  } else if (step === 'report' && outcome) {
    body = html`<${ImportReport} summary=${summary} cancelled=${outcome.cancelled}
      notSent=${outcome.results.filter((result) => result.status === 'CANCELLED').length}
      counters=${mode === 'family' ? outcome.counters : null} createdRelations=${outcome.createdRelations} hasErrors=${failures.length > 0}
      onDownloadErrors=${() => downloadCsv(
        buildErrorCsv({ headers, dataRows, firstRowNumber, failures }),
        toFileName(t('spreadsheetImport.report.errorsFileName', { object: objectLabel })),
      )} />`;
    footer = html`
      <${Button} title=${t('spreadsheetImport.report.importAnother')} size="small" variant="secondary" onClick=${restart} />
      <${Button} title=${t('spreadsheetImport.report.done')} size="small" accent="blue" onClick=${close} />`;
  }

  return createPortal(
    html`<div class="modal-backdrop spreadsheet-import-backdrop" onMouseDown=${(/** @type {MouseEvent} */ event) => event.target === event.currentTarget && step === 'upload' && close()}>
      <div class="spreadsheet-import-dialog" role="dialog" aria-modal="true" aria-label=${title}>
        <header class="spreadsheet-import-header">
          <div class="spreadsheet-import-title">
            <${Icon} name="IconFileImport" size=${18} />
            <h2>${title}</h2>
            ${file ? html`<span class="spreadsheet-import-file-name" title=${file.name}>${file.name}</span>` : null}
          </div>
          <ol class="spreadsheet-import-steps">
            ${STEP_INDICATORS.map(([key], index) => html`<li key=${key} data-state=${index < currentIndicator ? 'done' : index === currentIndicator ? 'current' : undefined}>
              <span class="spreadsheet-import-step-number">${index < currentIndicator ? html`<${Icon} name="IconCheck" size=${12} />` : index + 1}</span>
              ${t(`spreadsheetImport.steps.${key}`)}
            </li>`)}
          </ol>
          <${IconButton} icon="IconX" size="small" variant="tertiary" ariaLabel=${t('common.close')} disabled=${step === 'importing'} onClick=${close} />
        </header>
        <div class="spreadsheet-import-body">${body}</div>
        ${footer ? html`<footer class="spreadsheet-import-footer">${footer}</footer>` : null}
      </div>
    </div>`,
    document.body,
  );
}
